import { useState, useEffect } from 'react'
import { compact } from 'lodash'

import { SubscriptionType } from '@/redux/ducks/subscription/types/details'
import { trackOmoSubscription } from '@/modules/base/helpers/omoSubscriptionTracker'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import { trackOrgamaxGaEvent } from '@/modules/base/helpers/trackOrgamaxGaEvent'
import navigateToCurrentPage from '@/utils/navigateToCurrentPage'
import { useLocation, useNavigate } from 'react-router'
import lang from '@/lang'
import { useAppDispatch } from '@/redux/store'
import {
  STEPS,
  setActiveStep as storeSetActiveStep,
  setStepsInOrder as storeSetStepsInOrder,
  setCompletedSteps as storeSetCompletedSteps,
  setShowModules as storeSetShowModules,
  setSubscriptionTypes as storeSetSubscriptionTypes,
  resetState,
} from '@/redux/ducks/upgrade/wizard'

type SubscriptionStepType = SubscriptionType['typeOfSubscription']
export type StepType = SubscriptionStepType | 'extensions' | 'billingForm' | 'success'

interface UpgradeStepProps {
  activeSubscriptions: SubscriptionType[]
  onClose: () => void
  showAllModules: boolean
  skipAllModulesSelection?: boolean,
  isCartUpdated?: boolean,
}

export const StepButtonTitles = {
  [STEPS.SUBSCRIPTION_MAIN]: lang.upgradeModal.upgradeSteps.button.subscriptionMain,
  [STEPS.SUBSCRIPTION_DOCUMENT]: lang.upgradeModal.upgradeSteps.button.subscriptionDocument,
  [STEPS.SUBSCRIPTION_STAFF]: lang.upgradeModal.upgradeSteps.button.subscriptionStaff,
  [STEPS.EXTENSIONS]: lang.upgradeModal.upgradeSteps.button.extensions,
  [STEPS.BILLING]: lang.upgradeModal.upgradeSteps.button.billing,
}

export const subscriptionSteps: StepType[] = [
  STEPS.SUBSCRIPTION_MAIN,
  STEPS.SUBSCRIPTION_DOCUMENT,
  STEPS.SUBSCRIPTION_STAFF,
]

export default function useUpgradeSteps(props: UpgradeStepProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { activeSubscriptions, onClose, showAllModules, isCartUpdated = false, skipAllModulesSelection = false } = props

  // DO-13587: the checkout ends with "Daten & Zahlung", which also holds the purchase button.
  // The step is therefore never skipped - existing billing data is only used to prefill it.
  const activeSubscriptionTypes = activeSubscriptions.map(sub => sub.typeOfSubscription)
  const subscriptionStepsToInclude = showAllModules
    ? [STEPS.EXTENSIONS]
    : [...subscriptionSteps.filter((sub: SubscriptionStepType) => activeSubscriptionTypes.includes(sub)), STEPS.EXTENSIONS]
  const stepsInOrderFromProps = compact([
    ...subscriptionStepsToInclude,
    STEPS.BILLING,
  ]) as StepType[]
  const [stepsInOrder, setStepsInOrder] = useState<StepType[]>(stepsInOrderFromProps as StepType[])
  const [activeStep, setActiveStep] = useState<StepType>(stepsInOrder[0] as StepType)
  const [showModules, setShowModules] = useState<boolean>(false)
  const [completedSteps, setCompletedSteps] = useState<Array<number>>([])
  const [subscriptionTypes, setSubscriptionTypes] = useState([])
  const activeStepIndex = stepsInOrder.indexOf(activeStep)

  useEffect(() => {
    if (subscriptionSteps.includes(activeStep)) {
      const selectedSub = activeSubscriptions.find(sub => sub.typeOfSubscription === activeStep)
      if (selectedSub) {
        getSubscriptionTypes(selectedSub.typeOfSubscription)
      }
    }
  }, [activeStep])

  useEffect(() => {
    dispatch(storeSetActiveStep(activeStep))
  }, [activeStep])
  useEffect(() => {
    dispatch(storeSetStepsInOrder(stepsInOrder))
  }, [stepsInOrder])
  useEffect(() => {
    dispatch(storeSetCompletedSteps(completedSteps))
  }, [completedSteps])
  useEffect(() => {
    dispatch(storeSetSubscriptionTypes(subscriptionTypes))
  }, [subscriptionTypes])
  useEffect(() => {
    dispatch(storeSetShowModules(showModules))
  }, [showModules])

  useEffect(() => {
    return () => {
      dispatch(resetState())
    }
  }, [])

  useEffect(() => {
    if (skipAllModulesSelection && !isCartUpdated) {
      setActiveStep(stepsInOrder[0])
    }
  }, [skipAllModulesSelection])

  const getSubscriptionTypes = (subscriptionType) => {
    SubscriptionService.getWebSubscriptionTypes(subscriptionType).then((body) => {
      setSubscriptionTypes(body.data)
    })
  }

  const goToNextStep = (_, skip = false, addExtension = false) => {
    if (subscriptionSteps.includes(activeStep) && skip) {
      trackOrgamaxGaEvent('tester-zu-kauf_funnel', 'Weiter', `Ohne ${activeStep} fortfahren`)
    } else if (activeStep === STEPS.BILLING) {
      trackOrgamaxGaEvent('tester-zu-kauf_funnel', 'Weiter', `Von ${activeStep} weiter`)
    }
    let stepList = stepsInOrder
    if (addExtension) {
      if (!stepsInOrder.includes(STEPS.EXTENSIONS)) {
        // put it before BILLING
        const billingIndex = stepsInOrder.indexOf(STEPS.BILLING)
        stepList = [...stepsInOrder.slice(0, billingIndex), STEPS.EXTENSIONS, ...stepsInOrder.slice(billingIndex)]
        setStepsInOrder(stepList)
      }
    }

    if (activeStepIndex === stepList.length) return
    setActiveStep(stepList[activeStepIndex + 1])
    if (!skip) setCompletedSteps(prevState => [...prevState, activeStepIndex])
    setShowModules(false)
  }

  const handlePrevious = () => {
    switch (activeStep) {
      case STEPS.BILLING:
        trackOrgamaxGaEvent(
          'tester-zu-kauf_funnel',
          'Zurück',
          'Von_abrechnungsinformation_zurück',
        )
        break

      case STEPS.SUBSCRIPTION_MAIN:
      case STEPS.SUBSCRIPTION_DOCUMENT:
      case STEPS.SUBSCRIPTION_STAFF:
        trackOrgamaxGaEvent(
          'tester-zu-kauf_funnel',
          'Zurück',
          `Von_${activeStep}_zurück`,
        )
    }
    // DO-13587: going back keeps the cart - choosing another plan in step 1 replaces the selected one
    const previousStepIndex = activeStepIndex - 1
    setActiveStep(stepsInOrder[previousStepIndex])
    setCompletedSteps(prevState => prevState.filter(index => index !== previousStepIndex))
  }

  const hanldeFinished = () => {
    onClose()
    navigateToCurrentPage(navigate, location)
  }

  const handleCancel = () => {
    switch (activeStep) {
      case STEPS.BILLING:
        trackOmoSubscription(
          'click_abrechnungsinformation',
          'abrechnungsinformation_abbrechen',
        )
        break

      case STEPS.SUBSCRIPTION_MAIN:
      case STEPS.SUBSCRIPTION_DOCUMENT:
      case STEPS.SUBSCRIPTION_STAFF:
        trackOmoSubscription(
          'click_erweiterungsauswahl',
          `erweiterungsauswahl_${activeStep}_abbrechen`,
        )
    }

    onClose?.()
  }

  return {
    activeStep,
    stepsInOrder,
    goToNextStep,
    handleCancel,
    handlePrevious,
    hanldeFinished,
    subscriptionTypes,
    showModules,
    setShowModules,
    completedSteps,
  }
}
